import axios from "axios";
import { useAuthStore } from "@/store/auth";
import { getDeviceId } from "@/utils/deviceId";
import { queryClient } from "@/api/queryClient";
import { rememberPostLoginTarget } from "@/utils/postLoginRedirect";
import { getJWTExp as readJWTExp } from "@/utils/jwt";
import { noteServerDate } from "@/utils/serverClock";

const apiClient = axios.create({
  baseURL: import.meta.env.VITE_API_URL ?? "/api/v1",
  headers: { "Content-Type": "application/json" },
});

/**
 * For the endpoints that ESTABLISH a session: login, SMS verification, refresh.
 *
 * Deliberately interceptor-free. Going through apiClient meant that a leftover
 * `elg5-auth` entry in localStorage — a token from last week, a half-written pair —
 * made the request interceptor try to refresh it BEFORE the login request left the
 * browser, and bounce to /auth/login when that failed. The login POST then never
 * reached the server at all: 185 of 299 hits on the login screen in the week to
 * 12.09.2026 were the login page hard-reloading itself, with nothing to show for it
 * in the access log. Signing in must not depend on the session being signed out of.
 */
export const authClient = axios.create({
  baseURL: import.meta.env.VITE_API_URL ?? "/api/v1",
  headers: { "Content-Type": "application/json" },
});

// A token this close to expiry (or past it) is dead by the time the request reaches
// the server — it can only come back as a 401. Clock skew between browser and server
// is tolerated the cheap way: a token that merely *looks* expired here is refreshed a
// little early, which costs one round trip and nothing else.
const ACCESS_EXPIRY_MARGIN_MS = 5_000;

function accessTokenExpired(token: string): boolean {
  const exp = readJWTExp(token);
  return exp !== null && exp * 1000 <= Date.now() + ACCESS_EXPIRY_MARGIN_MS;
}

/**
 * The current access token, or null when there is none or it is known to be expired.
 * For fire-and-forget senders that bypass apiClient (keepalive fetches on pagehide):
 * they cannot wait for a refresh, so they must not go out with a dead token either.
 */
export function getLiveAccessToken(): string | null {
  const token = useAuthStore.getState().accessToken;
  if (!token || accessTokenExpired(token)) return null;
  return token;
}

/**
 * Rejection for a request that was never sent: its access token had expired and could
 * not be refreshed. Deliberately not an AxiosError — the response interceptor must not
 * mistake it for a 401 from the server and try to refresh-and-replay it.
 */
export class ExpiredSessionError extends Error {
  readonly cause: unknown;

  constructor(cause: unknown) {
    super("access_token_expired");
    this.name = "ExpiredSessionError";
    this.cause = cause;
  }
}

apiClient.interceptors.request.use(async (config) => {
  let token = useAuthStore.getState().accessToken;
  if (token && accessTokenExpired(token)) {
    // Refresh BEFORE the request leaves, not after the server bounces it. Background
    // traffic — pollers, heartbeats, the burst of queries a page fires on mount — used
    // to go out with a token that had expired in place (a tab left open overnight, a
    // laptop woken from sleep, a throttled background tab), collect a 401 each and only
    // then refresh and replay. Every one of those 401s reached the server, which logged
    // it as an anonymous token_not_valid. Now the whole burst waits on the one shared
    // refresh and goes out once, with a live token.
    try {
      token = await doRefresh();
    } catch (err) {
      const rescued = rescueTokensFromStorage();
      if (rescued) {
        token = rescued;
      } else {
        logoutIfSessionIsReallyOver(err);
        // A dead token cannot be served, so not sending is strictly better than a
        // guaranteed 401 — background requests in particular have nothing to gain
        // from knocking on the server with credentials it is bound to refuse.
        throw new ExpiredSessionError(err);
      }
    }
  }
  if (token) config.headers.Authorization = `Bearer ${token}`;
  config.headers["X-Device-ID"] = getDeviceId();
  return config;
});

// Single in-flight refresh promise shared across all concurrent 401s within this tab.
// Refresh tokens rotate, so a burst of 401s must collapse into one round trip rather
// than each racing to spend the same token.
let refreshPromise: Promise<string> | null = null;

const REFRESH_LOCK_KEY = "elg5-refresh-lock";
const REFRESH_LOCK_TTL_MS = 15_000;
// How long to wait for another tab's in-flight refresh before giving up and doing
// our own. Shorter than the lock TTL so a single-window PWA that was killed while
// holding the lock doesn't stall the first request after relaunch.
const REFRESH_WAIT_MS = 5_000;

// Refusals of one specific refresh token that were not "this token is spent" —
// a raced rotation, say. Bounded, so a token the server rejects for some reason we
// can't see still ends the session instead of retrying forever. Counted per token:
// once a rotation succeeds the new token starts with a full budget.
let softRefreshFailures = 0;
let softFailureToken: string | null = null;
const MAX_SOFT_REFRESH_FAILURES = 3;

function clearSoftFailures(): void {
  softRefreshFailures = 0;
  softFailureToken = null;
}

function readStoredTokens(): { accessToken: string | null; refreshToken: string | null } {
  try {
    const raw = localStorage.getItem("elg5-auth");
    if (!raw) return { accessToken: null, refreshToken: null };
    const parsed = JSON.parse(raw);
    return {
      accessToken: parsed?.state?.accessToken ?? null,
      refreshToken: parsed?.state?.refreshToken ?? null,
    };
  } catch {
    return { accessToken: null, refreshToken: null };
  }
}

function tryAcquireRefreshLock(): boolean {
  const now = Date.now();
  const raw = localStorage.getItem(REFRESH_LOCK_KEY);
  if (raw && now - Number(raw.split(":")[0]) < REFRESH_LOCK_TTL_MS) return false;
  // localStorage has no compare-and-set, so read-then-write lets two tabs both
  // see a free lock and both claim it. Stamping a value unique to this call and
  // reading it back collapses that: every racing tab observes the same final
  // value, so exactly one of them sees its own stamp and proceeds.
  const stamp = `${now}:${Math.random().toString(36).slice(2)}`;
  localStorage.setItem(REFRESH_LOCK_KEY, stamp);
  return localStorage.getItem(REFRESH_LOCK_KEY) === stamp;
}

function releaseRefreshLock(): void {
  localStorage.removeItem(REFRESH_LOCK_KEY);
}

// Wait (event-driven) for another tab to finish its refresh and write new tokens.
// Resolves with the new access token, or null if timed out / other tab failed.
function waitForCrossTabRefresh(oldRefresh: string): Promise<string | null> {
  return new Promise((resolve) => {
    const timer = setTimeout(() => {
      window.removeEventListener("storage", handler);
      resolve(null);
    }, REFRESH_WAIT_MS);

    function handler(e: StorageEvent) {
      if (e.key !== "elg5-auth" && e.key !== REFRESH_LOCK_KEY) return;
      const stored = readStoredTokens();
      if (stored.refreshToken && stored.refreshToken !== oldRefresh && stored.accessToken) {
        clearTimeout(timer);
        window.removeEventListener("storage", handler);
        useAuthStore.getState().setTokens(stored.accessToken, stored.refreshToken);
        resolve(stored.accessToken);
        return;
      }
      // Lock was released but tokens didn't change — other tab failed
      if (e.key === REFRESH_LOCK_KEY && !e.newValue) {
        clearTimeout(timer);
        window.removeEventListener("storage", handler);
        resolve(null);
      }
    }

    window.addEventListener("storage", handler);
  });
}

type TokenPair = { access?: string; refresh?: string };

// Derived from the same base as every other call rather than hardcoded, so refresh
// can't quietly point somewhere else if the API ever moves off the app's own origin.
// Sent on a bare axios instance on purpose: going through apiClient would re-enter
// the 401 interceptor and recurse.
const REFRESH_URL = `${apiClient.defaults.baseURL ?? "/api/v1"}/auth/token/refresh/`;

function executeRefresh(refreshToken: string): Promise<string> {
  return axios
    .post<TokenPair & { data?: TokenPair }>(REFRESH_URL, { refresh: refreshToken })
    .then(({ data: body }) => {
      // Every endpoint here answers in the { data, errors, meta } envelope, and the
      // refresh endpoint is no exception — reading `access` off the top level yielded
      // undefined, so the rotated pair was dropped on the floor while the server had
      // already retired the old refresh token. That silently ended a session on every
      // single refresh. The fallback to the bare body keeps this working if the
      // endpoint is ever served unwrapped.
      const pair = body?.data ?? body;
      const access = pair?.access;
      if (!access) {
        // Treated as a failed refresh rather than written to the store: a corrupt
        // session is worse than a retry.
        return Promise.reject(new Error("refresh_response_missing_access"));
      }
      useAuthStore.getState().setTokens(access, pair?.refresh ?? refreshToken);
      clearSoftFailures();
      return access;
    });
}

function doRefresh(): Promise<string> {
  if (refreshPromise) return refreshPromise;

  const inMemoryRefresh = useAuthStore.getState().refreshToken;
  if (!inMemoryRefresh) return Promise.reject(new Error("no_refresh_token"));

  // Fast path: another tab already rotated the pair and wrote it to localStorage.
  const stored = readStoredTokens();
  if (stored.refreshToken && stored.refreshToken !== inMemoryRefresh && stored.accessToken) {
    useAuthStore.getState().setTokens(stored.accessToken, stored.refreshToken);
    return Promise.resolve(stored.accessToken);
  }

  // Cross-tab lock: if another tab is already mid-refresh, wait for its result
  // instead of spending a second round trip on the same token.
  if (!tryAcquireRefreshLock()) {
    refreshPromise = waitForCrossTabRefresh(inMemoryRefresh)
      .then((token) => token ?? executeRefresh(inMemoryRefresh))
      .finally(() => {
        refreshPromise = null;
      });
    return refreshPromise;
  }

  refreshPromise = executeRefresh(inMemoryRefresh)
    .finally(() => {
      releaseRefreshLock();
      refreshPromise = null;
    });

  return refreshPromise;
}

/**
 * Clear credentials + client state and bounce to login. Reserved for a session that
 * is actually over — a spent refresh token or an account the server refuses. Callers
 * reach it through logoutIfSessionIsReallyOver() so the response interceptor,
 * proactiveRefresh, and the auto-refresh scheduler stay in lockstep.
 */
function forceLogoutRedirect(): void {
  clearSoftFailures();
  // Remember the page so the user resumes there after logging back in — notably a
  // GG notification opened on a session that had already lapsed.
  rememberPostLoginTarget(window.location.pathname + window.location.search);
  useAuthStore.getState().logout();
  queryClient.clear();
  localStorage.removeItem("elg5-notif-leader-ts");
  // Tear down the WebSocket too so we don't reconnect with a stale token.
  // Dynamic import keeps client.ts free of a static dependency on the
  // chat feature (chat imports apiClient from here).
  import("@/features/chat/api/realtime")
    .then((m) => m.disconnectRealtime())
    .catch(() => {});
  window.location.href = "/auth/login";
}

/**
 * Another tab won a concurrent refresh and wrote the rotated pair to localStorage
 * while ours was in flight. Adopt it and return the fresh access token.
 */
function rescueTokensFromStorage(): string | null {
  const stored = readStoredTokens();
  const current = useAuthStore.getState().refreshToken;
  if (stored.refreshToken && stored.refreshToken !== current && stored.accessToken) {
    useAuthStore.getState().setTokens(stored.accessToken, stored.refreshToken);
    // Inheriting a live pair proves the session is fine, same as refreshing it.
    clearSoftFailures();
    return stored.accessToken;
  }
  return null;
}

function refreshTokenStillValid(): boolean {
  const token = useAuthStore.getState().refreshToken;
  if (!token) return false;
  const exp = readJWTExp(token);
  // Unparseable token: treat as dead rather than retrying against it forever.
  return exp !== null && exp * 1000 > Date.now();
}

/**
 * Decide what a failed refresh means. Only a refresh token that is genuinely spent
 * ends the session — anything else (offline, a raced rotation, a blip on the auth
 * endpoint) is a transient failure, and logging the user out for it is exactly the
 * bug that had everyone re-entering their password every morning.
 *
 * Returns true when the session was terminated; the caller should stop.
 */
function logoutIfSessionIsReallyOver(err: unknown): boolean {
  const status = axios.isAxiosError(err) ? err.response?.status : undefined;

  // No response at all — backend unreachable. Retry on the next request.
  if (axios.isAxiosError(err) && !err.response) return false;

  // 5xx is the server failing, not the credential being refused. A deploy or a bad
  // minute upstream must never sign the whole company out, so these don't even count
  // against the attempt budget.
  if (status !== undefined && status >= 500) return false;

  const token = useAuthStore.getState().refreshToken;
  if (token && refreshTokenStillValid()) {
    if (softFailureToken !== token) {
      softFailureToken = token;
      softRefreshFailures = 0;
    }
    if (++softRefreshFailures < MAX_SOFT_REFRESH_FAILURES) return false;
  }

  forceLogoutRedirect();
  return true;
}

// The server's clock, for checks against server-issued timestamps (utils/serverClock.ts).
apiClient.interceptors.response.use(
  (response) => {
    noteServerDate(response.headers?.["date"]);
    return response;
  },
  (error) => {
    noteServerDate(error?.response?.headers?.["date"]);
    return Promise.reject(error);
  },
);

apiClient.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config as typeof error.config & { _retry?: boolean };

    if (error.response?.status === 403 && error.response.data?.meta?.code === "sms_verification_required") {
      window.location.href = "/auth/sms-verify";
      return Promise.reject(error);
    }

    // A 401 on a request that went out WITHOUT credentials is not a dead session —
    // there was no session in it to kill. ProtectedLayout's profile fetch used to
    // race ahead of its own "no tokens → login screen" check, collect this 401 and
    // hand it to the logout path, which then hard-reloaded the page out from under
    // whoever was typing their password.
    const carriedCredentials = Boolean(originalRequest?.headers?.Authorization);

    if (error.response?.status === 401 && !carriedCredentials) {
      return Promise.reject(error);
    }

    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;

      try {
        const newAccess = await doRefresh();
        originalRequest.headers.Authorization = `Bearer ${newAccess}`;
        return apiClient(originalRequest);
      } catch (refreshError) {
        // Truly concurrent cross-tab race: both tabs hit 401 at the same millisecond
        // and both tried to refresh. The other tab won, but it also wrote fresh
        // tokens to localStorage. Rescue before considering the session over.
        const rescued = rescueTokensFromStorage();
        if (rescued) {
          originalRequest.headers.Authorization = `Bearer ${rescued}`;
          return apiClient(originalRequest);
        }

        logoutIfSessionIsReallyOver(refreshError);
        return Promise.reject(error);
      }
    }

    // Fresh token was issued but the retried request still got 401 (e.g. account
    // disabled server-side). The token will never work — clear it and logout.
    if (error.response?.status === 401 && originalRequest._retry) {
      forceLogoutRedirect();
    }

    return Promise.reject(error);
  }
);

export { getJWTExp } from "@/utils/jwt";

/** Call on tab focus / visibility change. Silently refreshes when token is expired or within 60 s of expiry. */
export function proactiveRefresh(): void {
  const token = useAuthStore.getState().accessToken;
  if (!token) return;
  const exp = readJWTExp(token);
  if (exp !== null && exp * 1000 < Date.now() + 60_000) {
    doRefresh().catch((err) => {
      // Another tab may have rotated the pair while ours was in flight.
      if (rescueTokensFromStorage()) return;
      logoutIfSessionIsReallyOver(err);
    });
  }
}

// ── Proactive auto-refresh scheduler ──────────────────────────────────────────
// proactiveRefresh() above only runs on `visibilitychange`. A tab kept focused all
// day never fires that event, so its access token expires in place and the next
// background poll (notifications, presence, …) goes out with a dead token → 401 →
// refresh → retry — a spurious 401 logged every token lifetime, per open tab.
// This timer refreshes ~60 s before expiry and reschedules off the rotated token,
// so requests essentially never fire expired. It reuses doRefresh()'s cross-tab
// lock, so N open tabs still collapse to a single network refresh.

let autoRefreshTimer: ReturnType<typeof setTimeout> | null = null;
let schedulerActive = false;

function scheduleNextRefresh(): void {
  if (autoRefreshTimer) { clearTimeout(autoRefreshTimer); autoRefreshTimer = null; }
  if (!schedulerActive) return;
  const token = useAuthStore.getState().accessToken;
  if (!token) return;
  const exp = readJWTExp(token);
  if (exp === null) return;
  // Fire 60 s before expiry; clamp to ≥1 s so an already-expired token refreshes now.
  const delay = Math.max(1_000, exp * 1000 - Date.now() - 60_000);
  autoRefreshTimer = setTimeout(() => {
    if (!schedulerActive) return;
    doRefresh()
      .then(() => { if (schedulerActive) scheduleNextRefresh(); })
      .catch((err) => {
        if (!schedulerActive) return;
        // Another tab may have rotated the pair while ours was in flight — adopt it
        // and reschedule off the token we just inherited.
        if (rescueTokensFromStorage()) {
          scheduleNextRefresh();
          return;
        }
        // Offline, or a transient failure on a refresh token that is still valid:
        // don't log out, just try again shortly.
        if (!logoutIfSessionIsReallyOver(err)) {
          autoRefreshTimer = setTimeout(scheduleNextRefresh, 30_000);
        }
      });
  }, delay);
}

/** Start (or reschedule) the proactive token-refresh timer off the current access token. */
export function startTokenAutoRefresh(): void {
  schedulerActive = true;
  scheduleNextRefresh();
}

/** Stop the proactive token-refresh timer (e.g. on logout / layout unmount). */
export function stopTokenAutoRefresh(): void {
  schedulerActive = false;
  if (autoRefreshTimer) { clearTimeout(autoRefreshTimer); autoRefreshTimer = null; }
}

export default apiClient;
